import { AfterViewInit, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, inject } from '@angular/core';
import { forkJoin, take } from 'rxjs';
import { ColDef, GridOptions, RowSelectedEvent } from 'ag-grid-community';
import { Project } from 'src/app/shared/models/project';
import { Constants } from 'src/app/shared/utils/constants';
import { GridComponent } from 'src/app/shared/components/grid/grid.component';
import { EEAOutputProjectInputsApiService } from 'src/app/shared/services/eea-output-project-inputs.api.service';
import { EEAOutputProjectSummaryResultSurfaceWaterApiService } from 'src/app/shared/services/out-put-project/eea-output-project-step1n2-summary-result.api.service';
import { EEAOutputProjectLogicService } from '../../eea-output-project.logic.service';
import { EEAOutputProjectSwashSwanResultsColdef } from './eea-output-project-swash-swan-results.coldef';
import { RunApiService } from 'src/app/shared/services/run.api.service';
import { RunDetails } from 'src/app/shared/models/run/run-details';

@Component({
  selector: 'app-eea-output-project-swash-swan-results',
  templateUrl: './eea-output-project-swash-swan-results.component.html',
  styleUrls: ['./eea-output-project-swash-swan-results.component.css']
})
export class EEAOutputProjectSwashSwanResultsComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() selectedProject?: Project;
  @Input() selectedModel?: string;
  @Input() isProjectOwnershipValid = false;

  @ViewChild('grid') grid!: GridComponent;

  columnDef?: ColDef[];
  rowData: any[] = [];
  loading = true;

  pivotEnabled: boolean = true;
  private pivotListener: (() => void) | null = null;
  private readonly cdr = inject(ChangeDetectorRef);
  private initialColumnState: any[] | null = null;
  private lastCapturedGridApi: any = null;
  private readonly pivotDynamicColumns = ['DominantEntryRoute', 'SevenDaysPecSwTwa', 'MaxPecSed', 'PecSwTwa21Days', 'GlobalMaxSwDate', 'ApplicationDate'];

  sideBar = {
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
        toolPanelParams: {
          suppressRowGroups: false,
          suppressValues: false,
          suppressPivots: false,
          suppressPivotMode: false,
          suppressColumnSelectAll: true,
          suppressColumnExpandAll: true,
          suppressFilters: false,
          contractSelection: true
        }
      },
      {
        id: 'filters',
        labelDefault: 'Filters',
        labelKey: 'filters',
        iconKey: 'filter',
        toolPanel: 'agFiltersToolPanel',
        toolPanelParams: {
          suppressFilterSearch: false,
          suppressSelectAll: false
        }
      }
    ],
    defaultToolPanel: 'columns'
  };

  autoGroupColumnDef = {
    cellRendererParams: {
      suppressCount: true
    }
  };

  gridOptions: GridOptions = {
    pivotHeaderHeight: 40,
    pivotGroupHeaderHeight: 34,
    processPivotResultColDef: (colDef: any) => {
      switch (colDef.field) {
        case 'MaxPecSw': colDef.headerName = 'Max PECsw (µg/L)'; break;
      }
      colDef.headerComponent = 'compoundHeader';
    }
  };

  private readonly logicService = inject(EEAOutputProjectLogicService);
  private readonly colDef = inject(EEAOutputProjectSwashSwanResultsColdef);
  private readonly inputsService = inject(EEAOutputProjectInputsApiService);
  private readonly summaryService = inject(EEAOutputProjectSummaryResultSurfaceWaterApiService);
  private readonly runApiService = inject(RunApiService);

  get showResults(): boolean {
    return this.selectedProject?.status === 2;
  }

  get hideBufferDistance(): boolean {
    return this.selectedModel === Constants.MODELS.SWASH;
  }

  get isStep4(): boolean {
    return this.selectedModel === Constants.MODELS.SWAN;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedProject'] || changes['selectedModel'] || changes['isProjectOwnershipValid']) {
      this.loadData();
    }
  }

  private loadData(): void {
    if (!this.selectedProject?.projectPk || !this.selectedModel) {
      this.rowData = [];
      this.loading = false;
      return;
    }

    this.loading = true;
    this.columnDef = this.colDef.getColumnsDefinition(this.showResults, this.hideBufferDistance, this.isProjectOwnershipValid, this.isStep4);

    const runDetails$ = this.runApiService.getRunDetailsByProjectAndModel(this.selectedProject.projectPk, this.selectedModel);
    const results$ = this.summaryService.getSummariesResultsSurfaceWaterByModel(this.selectedProject.projectPk, this.selectedModel);
    
    if (this.showResults) {
      forkJoin([runDetails$, results$]).pipe(take(1)).subscribe({
        next: ([runDetails, results]) => {
          const mappedInputs = this.mapRunDetailsToGridData(runDetails);
          const surfaceWaterResults = (results as any)?.Value || results || [];
          this.rowData = this.mergeData(mappedInputs, surfaceWaterResults);
          this.loading = false;
          this.refreshSelectedValuesTimeout();
        },
        error: () => this.loading = false
      });
    } else {
      runDetails$.pipe(take(1)).subscribe({
        next: (runDetails) => {
          this.rowData = this.mapRunDetailsToGridData(runDetails);
          this.loading = false;
        },
        error: () => this.loading = false
      });
    }
  }

  private mapRunDetailsToGridData(runDetails: RunDetails[]): any[] {
    if (!runDetails || runDetails.length === 0) return [];

    const isSwan = this.selectedModel === Constants.MODELS.SWAN;
    const result: any[] = [];

    runDetails.forEach(rd => {
      const run = rd.runDetails || {};
      const applicationSchemeData = run.applicationSchemeData || {};
      const model = run.model || {};
      const dataSet = run.dataSet || {};
      const modelCropXScenario = run.modelCropXScenario || {};
      const gapXRun = run.gapXRun || {};
      const endpointXRun = run.endpointXRun || {};
      const applicationWindows = applicationSchemeData.applicationSchemeXApplicationWindow || [];

      const rate = this.getRate(applicationSchemeData, run);

      const gapData = {
        runId: rd.runId || run.id,
        runPk: run.runPk || run.RunPk,
        model: rd.model || model.name,
        applicationSchemeName: applicationSchemeData.name || run.applicationScheme,
        crop: run.cropDriftInterceptionName || applicationSchemeData.cropDriftInterceptionName,
        applicationsNumber: applicationSchemeData.numberOfApplications || run.numberOfApplications,
        applicationInterval: applicationSchemeData.applicationInterval || run.applicationInterval,
        rate: rate,
        BBCHEarliest: applicationSchemeData.bbchEarliest || run.bbchEarliest,
        scenarios: modelCropXScenario.scenario || applicationSchemeData.fullScenario,
        waterType: applicationSchemeData.waterType || run.waterType,
        applicationMethod: applicationSchemeData.applicationMethod || gapXRun.applicationMethod || run.applicationMethod,
        chemicalApplicationMethod: applicationSchemeData.chemicalApplicationMethod || gapXRun.chemicalApplicationMethod || run.chemicalApplicationMethod,
        soilDepthIncorporated: applicationSchemeData.soilDepth || gapXRun.soilDepthIncorporated || run.soilDepthIncorporated,
        endpointsDataSetName: dataSet.description || run.dataSetName,
      };

      const substanceName = run.alternativeCompoundName || run.activeIngredient;

      const parentEndpointData = {
        activeIngredient: run.activeIngredient,
        substanceName,
        molecularWeight: endpointXRun.molecularWeight || run.molecularWeight,
        saturatedVapourPressure: endpointXRun.saturatedVapourPressure || run.saturatedVapourPressure,
        solubilityInWater: endpointXRun.solubilityInWater || run.solubilityInWater,
        airDiffusionCoefficient: endpointXRun.airDiffusionCoefficient || run.airDiffusionCoefficient,
        waterDifussionCoefficient: endpointXRun.waterDiffusionCoefficient || run.waterDiffusionCoefficient,
        kfom: endpointXRun.kom || run.kom,
        kfoc: endpointXRun.kfoc || run.kfoc,
        freundlichSorptionExponentInSoil: endpointXRun.exponent || run.exponent,
        halfLifeInSoil: endpointXRun.halfLifeInSoil || run.halfLifeInSoil,
        halfLifeInWater: endpointXRun.halfLifeInWater || run.halfLifeInWater,
        halfLifeInSediment: endpointXRun.halfLifeinSediment || run.halfLifeinSediment,
      };

      const baseRowData = { ...gapData, ...parentEndpointData };

      const mitigations = run.mitigationsXApplicationScheme || run.applicationSchemeData?.mitigationsXApplicationScheme || [];

      const addRowsWithMitigationsAndLocations = (rowData: any) => {
        const locations = isSwan && applicationWindows.length > 0 
          ? applicationWindows.map((aw: any) => aw.location).filter((loc: any) => loc)
          : [null];
        
        locations.forEach((location: string | null) => {
          const rowDataWithLocation = { ...rowData, location: location || '' };
          
          if (isSwan && mitigations.length > 0) {
            mitigations.forEach((mitigation: any) => {
              result.push({
                ...rowDataWithLocation,
                ...this.getMitigationDataFromMitigation(mitigation)
              });
            });
          } else {
            const mitigationData = mitigations.length > 0 ? this.getMitigationDataFromMitigation(mitigations[0]) : {};
            result.push({
              ...rowDataWithLocation,
              ...mitigationData
            });
          }
        });
      };

      addRowsWithMitigationsAndLocations(baseRowData);
    });

    return result;
  }

  private getRate(applicationSchemeData: any, run: any): number | undefined {
    if (applicationSchemeData.applicationSchemeXActiveIngredientRate?.length > 0) {
      const activeIngredientRate = applicationSchemeData.applicationSchemeXActiveIngredientRate.find(
        (ai: any) => ai.moleculePk === run.activeIngredientPk
      );
      if (activeIngredientRate?.rate) {
        return activeIngredientRate.rate;
      }
    }
    return run.rate;
  }

  private getMitigationDataFromMitigation(mitigation: any): any {
    if (!mitigation) return {};
    
    return {
      mitigationName: mitigation.mitigationName || mitigation.name,
      bufferZone: mitigation.bufferZone ?? mitigation.vegetatedBufferWidth,
      sprayDriftOption: this.mapSprayDriftOption(mitigation.sprayDriftOption),
      nozzleReduction: mitigation.nozzleReduction,
      sprayDriftBuffer: mitigation.sprayDriftBuffer ?? mitigation.bufferWidth,
      distance: mitigation.distance ?? mitigation.distanceForDeposition
    };
  }

  private mapSprayDriftOption(sprayDriftOption: string | undefined): string {
    if (!sprayDriftOption) return '';
    return Constants.SPRAY_DRIFT_OPTION_DISPLAY_VALUES[sprayDriftOption] || sprayDriftOption;
  }

  private mergeData(inputs: any[], results: any[]): any[] {
    if (!results || results.length === 0) {
      return inputs;
    }

    const inputsByMitigation = new Map<string, any>();
    const inputsByRunAndSubstance = new Map<string, any>();
    const inputsByRunId = new Map<string, any>();

    inputs.forEach(input => {
      const runId = String(input.runId ?? '');
      const substance = String(input.substanceName ?? '');
      const mitigationKey = this.buildMitigationKey(runId, substance, input.sprayDriftBuffer, input.nozzleReduction, input.bufferZone);
      if (!inputsByMitigation.has(mitigationKey)) inputsByMitigation.set(mitigationKey, input);
      if (!inputsByRunAndSubstance.has(`${runId}|${substance}`)) inputsByRunAndSubstance.set(`${runId}|${substance}`, input);
      if (!inputsByRunId.has(runId)) inputsByRunId.set(runId, input);
    });

    return results.map(result => {
      const runId = String(result.runId ?? result.RunId ?? '');
      const substance = String(result.Compound ?? result.compound ?? '');
      const mitigationKey = this.buildMitigationKey(
        runId,
        substance,
        result.BufferDistance ?? result.bufferDistance,
        result.NozzleDriftReduction ?? result.nozzleDriftReduction,
        result.BufferZone ?? result.bufferZone
      );
      const matchingInput = inputsByMitigation.get(mitigationKey)
        ?? inputsByRunAndSubstance.get(`${runId}|${substance}`)
        ?? inputsByRunId.get(runId);

      return this.buildResultRow(result, matchingInput);
    });
  }

  private buildMitigationKey(runId: string, substance: string, buffer: any, nozzle: any, bufferZone: any): string {
    return [runId, substance, this.normalizeBuffer(buffer), this.normalizeBuffer(nozzle), this.normalizeBuffer(bufferZone)].join('|');
  }

  private buildResultRow(result: any, input: any): any {
    const isSwan = this.selectedModel === Constants.MODELS.SWAN;
    const enrichment = input ? this.getInputPropertiesToPreserve(input) : {};
    const waterbody = result.Waterbody ?? result.waterbody ?? '';
    const resultScenario = result.Scenarios ?? result.scenarios ?? '';

    let scenarios = enrichment.scenarios;
    if (isSwan && (!scenarios || scenarios === '')) {
      scenarios = `${resultScenario}_${waterbody}`;
    }

    return {
      ...enrichment,
      ...result,
      substanceName: result.Compound ?? result.compound ?? enrichment.substanceName,
      scenarios,
      bufferZone: result.BufferZone ?? result.bufferZone ?? enrichment.bufferZone,
      nozzleReduction: result.NozzleDriftReduction ?? result.nozzleDriftReduction ?? enrichment.nozzleReduction,
      sprayDriftBuffer: result.BufferDistance ?? result.bufferDistance ?? enrichment.sprayDriftBuffer,
    };
  }

  private normalizeBuffer(buffer: any): string {
    if (buffer === null || buffer === undefined || buffer === '' || buffer === 0 || 
        buffer === 'None' || buffer === 'none' || buffer === 'null' || buffer === '0') {
      return '';
    }
    return String(buffer).trim();
  }

  private getInputPropertiesToPreserve(input: any): any {
    return {
      runId: input.runId,
      model: input.model,
      applicationSchemeName: input.applicationSchemeName,
      crop: input.crop,
      applicationsNumber: input.applicationsNumber,
      applicationInterval: input.applicationInterval,
      rate: input.rate,
      BBCHEarliest: input.BBCHEarliest,
      scenarios: input.scenarios,
      waterType: input.waterType,
      applicationMethod: input.applicationMethod,
      chemicalApplicationMethod: input.chemicalApplicationMethod,
      soilDepthIncorporated: input.soilDepthIncorporated,
      endpointsDataSetName: input.endpointsDataSetName,
      activeIngredient: input.activeIngredient,
      substanceName: input.substanceName,
      molecularWeight: input.molecularWeight,
      saturatedVapourPressure: input.saturatedVapourPressure,
      solubilityInWater: input.solubilityInWater,
      airDiffusionCoefficient: input.airDiffusionCoefficient,
      waterDifussionCoefficient: input.waterDifussionCoefficient,
      kfom: input.kfom,
      kfoc: input.kfoc,
      freundlichSorptionExponentInSoil: input.freundlichSorptionExponentInSoil,
      halfLifeInSoil: input.halfLifeInSoil,
      halfLifeInWater: input.halfLifeInWater,
      halfLifeInSediment: input.halfLifeInSediment,
      location: input.location,
      mitigationName: input.mitigationName,
      bufferZone: input.bufferZone,
      sprayDriftOption: input.sprayDriftOption,
      nozzleReduction: input.nozzleReduction,
      sprayDriftBuffer: input.sprayDriftBuffer,
      distance: input.distance,
    };
  }

  onSelectedRowEvent(event: RowSelectedEvent): void {
    this.logicService.setSmeSelectionResultsSw(event);
  }

  onSelectedAllChanged(): void {
    if (this.grid) {
      this.logicService.setSmeSelectionsResultsSw(this.grid);
    }
  }

  onSignOffClicked(): void {
    if (this.grid) {
      this.logicService.signOffSmeSelectionsResultsSw(this.grid);
    }
  }

  private refreshSelectedValuesTimeout(): void {
    setTimeout(() => this.grid?.refreshSelectedValues('EeaSmeSelection'), 75);
  }

  ngAfterViewInit(): void {
    this.tryAttachPivotListener(0);
  }

  ngOnDestroy(): void {
    if (this.pivotListener && this.grid?.gridApi) {
      this.grid.gridApi.removeEventListener('columnPivotModeChanged', this.pivotListener);
    }
  }

  private tryAttachPivotListener(attempt: number): void {
    if (this.grid?.gridApi && this.grid?.gridColumnApi) {
      if (this.pivotListener) {
        this.grid.gridApi.removeEventListener('columnPivotModeChanged', this.pivotListener);
      }
      this.pivotListener = () => {
        this.pivotEnabled = !!this.grid.gridColumnApi.isPivotMode();
        if (!this.pivotEnabled) {
          this.cleanupGroupingAndPinning();
        } else if (this.initialColumnState) {
          this.restoreDefaultGroupingAndPinning();
        }
        this.applyPivotDynamicVisibility();
        this.cdr.detectChanges();
      };
      this.grid.gridApi.addEventListener('columnPivotModeChanged', this.pivotListener);
      this.pivotEnabled = !!this.grid.gridColumnApi.isPivotMode();
      this.cdr.detectChanges();
    } else if (attempt < 20) {
      setTimeout(() => this.tryAttachPivotListener(attempt + 1), 100);
    }
  }

  public onGridFirstDataRendered(): void {
    this.tryAttachPivotListener(0);

    const currentColumnApi = this.grid?.gridColumnApi;
    if (currentColumnApi && currentColumnApi !== this.lastCapturedGridApi) {
      this.lastCapturedGridApi = currentColumnApi;
      this.initialColumnState = currentColumnApi.getColumnState();
    }
    this.applyPivotDynamicVisibility();
  }

  private cleanupGroupingAndPinning(): void {
    if (!this.grid?.gridApi) return;
    this.grid.gridColumnApi.setRowGroupColumns([]);
    const allColumns = this.grid.gridColumnApi.getColumns() ?? [];
    if (allColumns.length > 0) {
      this.grid.gridColumnApi.applyColumnState({
        state: allColumns.map((col: any) => ({ colId: col.getColId(), pinned: null, sort: null, sortIndex: null }))
      });
    }
  }

  private restoreDefaultGroupingAndPinning(): void {
    if (!this.grid?.gridApi || !this.initialColumnState) return;
    const stateToApply = this.initialColumnState.map(col => ({
      colId: col.colId,
      rowGroup: col.rowGroup ?? false,
      pinned: col.pinned ?? null
    }));
    this.grid.gridColumnApi.applyColumnState({ state: stateToApply });
  }

  private applyPivotDynamicVisibility(): void {
    if (!this.grid?.gridColumnApi) return;
    const state = this.pivotDynamicColumns.map(colId => ({
      colId,
      hide: this.pivotEnabled
    }));
    this.grid.gridColumnApi.applyColumnState({ state });
  }
}
